import { Fragment, useEffect, useMemo, useRef, type ReactElement } from 'react';
import {
  Badge,
  Button,
  ColumnManager,
  EmptyState,
  Input,
  Panel,
  PanelFooter,
  PanelHeader,
  Skeleton,
  Table,
  TableWrap,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '@/components/ui';
import { InvoiceNumberLink } from '@/components/invoices/InvoiceNumberLink';
import { TextLink } from '@/components/ui/TextLink';
import { Tooltip } from '@/components/ui/Tooltip';
import { ViewToggle } from '@/components/ui/ViewToggle';
import { cx } from '@/lib/cx';
import { sumDecimalStrings } from '@/lib/decimal';
import { useRememberedState } from '@/lib/filter-memory/use-filter-memory';
import { formatDecimalString } from '@/lib/format';
import { buildRevenueMatrix, groupKeyOf } from '@/lib/revenue-matrix';
import { cycleColumnKey, isKeyShown, MATRIX_VISIBILITY_SCOPES, HIDE_ADD_ONS_ID } from '@/lib/matrix-visibility';
import { useAddOnProductRowKeys, useMatrixVisibility } from '@/lib/use-matrix-visibility';
import type { MatrixVisibilityProps } from '@/components/dashboards/RevenueMatrixTable';
import { CompanyRevenueMatrix } from './CompanyRevenueMatrix';
import { compactMoneyParts } from '@/lib/targets';
import { CompactFigure } from '@/components/dashboards/TargetTiles';
import { useColumnLayout, useFrozenOffsets } from '@/lib/use-column-layout';
import {
  paidFigureOf,
  type RevenueCurrency,
  type RevenueCycle,
} from '@/lib/use-revenue-analytics';
import {
  cycleDetailsOf,
  matrixCyclesOf,
  matrixLinesOf,
  readCompanyExpandView,
  regionLabelOf,
  reportsProducts,
  shareOf,
  shareText,
  writeCompanyExpandView,
  type CompanyExpandView,
  type ScopedRow,
} from './revenue-company-shared';

/**
 * THE RANKED COMPANY TABLE — who paid, how much of the total that is, and, one
 * click down, which projects and products it was for.
 *
 * ⚠️ **THIS IS THE ANALYTICAL READ, NOT A SECOND COPY OF `/revenue-tracker`.**
 * That page is the workbook: every company against every cycle, one cell per
 * pair, banded by billing region. This one answers a different question —
 * *which accounts is the money coming from, and how concentrated is it* — so it
 * is ranked rather than banded, one row per company rather than a block, and it
 * carries a share the cross-tab has no place for. Do not grow it back into a
 * grid; the grid already exists and is one nav row away.
 *
 * ── 🔴 THE DETAIL ROW IS THE ONLY PRODUCT-AND-PROJECT ANSWER THIS TAB HAS ──
 *
 * `serviceName` is a COMPOSED LABEL — the project's name where the line names a
 * project, otherwise the product's — and it is all the endpoint carries. So the
 * detail row LISTS what a company bought and does not, and must not, add it up
 * by product: the same product sold bare and sold through a project produces two
 * different strings, and a "revenue by product" figure built here would split
 * one product across several lines with nothing on screen to show it. Revenue by
 * product is its own tab with its own endpoint. See `cycleDetailsOf`.
 *
 * ⚠️ *SUPERSEDED IN PART 2026-10-15 (owner): the expanded company now OPENS on a
 * products × cycles matrix (`CompanyRevenueMatrix`), with a Table / Lines toggle
 * back to the list.* The matrix groups on `product.id` — never on `serviceName`,
 * so the objection above still holds for the label — and is withheld against a
 * server that reports no `product` (`reportsProducts`).
 *
 * ── ⚠️ WHAT THE COLUMNS MAY CLAIM ─────────────────────────────────────────
 *
 * Every figure is **one unit, paid invoices only, including tax** (owner,
 * 2026-10-16; it was ex-tax), within the chosen
 * cycle scope. ⚠️ *SUPERSEDED 2026-09-18, the mechanism: "one currency … this
 * tab's whole design is that two currencies are never added."* The tab CONVERTS
 * now, so the one unit is the base the SERVER echoed rather than the one the
 * reader filtered to — and two units are still never added, which is what the
 * conversion is for. The currency code renders against every amount rather than
 * only in the heading: a row read on its own, or screenshotted, has to carry its
 * own unit.
 *
 * ⚠️ **`alsoBilledIn` IS NOT DECORATION — ON AN UNCONVERTED RESPONSE.** A company
 * billed in two currencies is on this list twice, once under each, and neither
 * figure is what that account pays us. The badge is the only thing on screen that
 * says the number beside it is partial. **Converted, the array is empty and the
 * badge withdraws itself**, because the sibling row has been summed into this
 * one — see the comment at its render site.
 *
 * ── ⛔ THE "Cycles" COLUMN WAS REMOVED, AND IT IS NOT COMING BACK ───────────
 *
 * It printed how many cycles a company had revenue in, as a numeric column
 * between the billing region and the money. Three things were wrong with it and
 * only together do they settle it:
 *
 * 1. **It was already a column that half existed** — hidden whenever a single
 *    cycle was in scope, because it was then a column of ones. A table with two
 *    shapes is a table nobody can describe.
 * 2. **It is not money, and it sat next to two columns that are.** A numeric
 *    column competes for the eye with the figures either side of it, and this
 *    is a revenue ranking.
 * 3. **The expanded row already lists those cycles WITH their amounts.** The
 *    column was that list, counted — the lossier copy of something one click
 *    away.
 *
 * ⚠️ **THE FACT ITSELF SURVIVES, because it is the one that says whether a big
 * number is a durable account or one lucky year.** It is a quiet line under the
 * company name — where "how long have they been with us" belongs, beside the
 * company's identity rather than among its figures — and it appears only when
 * the scope spans more than one cycle, which is the same condition the column
 * had. What changed is where it sits, not whether it is said.
 *
 * ⛔ **AND IT IS NOT A HIDEABLE COLUMN EITHER.** The column manager offers no
 * key for it, deliberately: restoring it as something a reader can tick back on
 * would undo the decision above one reader at a time, with the three objections
 * unanswered.
 *
 * ── ⚠️ FIVE IS THE DECLARED SET, NOT THE RENDERED ONE ──────────────────────
 *
 * A reader chooses which of the five columns they see, in what order, and which
 * stick to an edge — stored against their account by `useColumnLayout`.
 * **Nothing in this file may count the columns**: the header, the body row and
 * the expanded row's `colSpan` all walk `layout.visibleKeys`, so a literal would
 * be wrong for every reader who has touched the control, and wrong silently.
 * See `COLUMN_KEYS` and `LOCKED_KEYS`.
 */

/** How many rows a fresh view shows before the reader asks for the rest. */
const HEAD_ROWS = 25;

/** A remembered list of row keys (filter memory), or `undefined`. */
function parseStringArray(raw: unknown): readonly string[] | undefined {
  return Array.isArray(raw) && raw.every((key) => typeof key === 'string')
    ? (raw as string[])
    : undefined;
}

/**
 * The link treatment for the invoice number, which renders through
 * `InvoiceNumberLink` rather than `ui/TextLink` and so takes its classes here.
 * Neutral text, underline on hover — the same look as `TextLink`. *SUPERSEDED
 * 2026-10-11: `text-accent-brand`* — the accent is reserved for primary actions,
 * the active nav row and focus.
 */
const LINK_CLASS =
  'text-fg underline-offset-2 transition-colors duration-150 ease-out hover:underline motion-reduce:transition-none';

/* -------------------------------------------------------------------------
 * The column model
 * --------------------------------------------------------------------- */

/**
 * Where this table's arrangement is stored, against the account.
 *
 * ⚠️ **THE SCOPE STRING IS THE STORAGE KEY**, so renaming it silently drops
 * every reader's saved arrangement — the same class of change as renaming a
 * sidebar group. It is not a label and nothing renders it.
 */
const LAYOUT_SCOPE = 'revenue.company';

/**
 * Every column, in its DECLARED order.
 *
 * ⚠️ **DECLARED, NOT RENDERED.** `useColumnLayout` treats this as the
 * authority: a key the reader has moved is honoured, a key this list has GAINED
 * lands in its declared position rather than at the end, and a key this list has
 * DROPPED disappears from a stored order rather than resurrecting a column.
 * Module-level so its identity is stable across renders.
 */
const COLUMN_KEYS = ['rank', 'company', 'region', 'revenue', 'share'] as const;

/**
 * 🔴 **`company` IS LOCKED BECAUSE IT HOLDS THE DISCLOSURE BUTTON**, not merely
 * because it identifies the row — and the two reasons are worth separating,
 * because only one of them is about capability.
 *
 * ⚠️ **THE EXPANDER IS NOT A COLUMN OF ITS OWN.** The chevron is rendered
 * *inside* the Company cell, beside the link, so the `w-12` cell at the head of
 * the row is the RANK and nothing else. Hiding `rank` costs a reader a number;
 * hiding `company` would take away the only route into the per-cycle detail
 * row — which is the one thing this tab has that `/revenue-tracker` does not.
 *
 * A locked key is still LISTED in the manager, disabled, so its absence cannot
 * read as the control having lost it.
 */
const LOCKED_KEYS = ['company'] as const;

/**
 * The manager's label per key.
 *
 * ⚠️ **`rank` READS "Revenue rank" HERE AND `#` IN THE HEADER**, which is the
 * same split the header cell already makes for assistive tech: the symbol is
 * legible in a 12-unit column and says nothing in a list of column names.
 */
const COLUMN_LABELS: Record<string, string> = {
  rank: 'Revenue rank',
  company: 'Company',
  region: 'Billing region',
  revenue: 'Paid revenue',
  share: 'Share',
};

function labelOf(key: string): string {
  return COLUMN_LABELS[key] ?? key;
}

/**
 * The two freeze props for one column, or nothing at all.
 *
 * ⚠️ **AN UNFROZEN CELL MUST CARRY NEITHER PROP**, not `frozen={undefined}` with
 * an offset beside it — `Table` reads the pair together and a positioned cell
 * with no reason to be positioned paints over the table's collapsed borders.
 */
function frozenPropsFor(
  key: string,
  frozenSideOf: (key: string) => 'left' | 'right' | undefined,
  offsets: Record<string, number>,
): { frozen?: 'left' | 'right'; frozenOffset?: number } {
  const side = frozenSideOf(key);
  if (side === undefined) return {};
  return { frozen: side, frozenOffset: offsets[key] ?? 0 };
}

function ChevronIcon({ open }: { open: boolean }): ReactElement {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={cx(
        'h-4 w-4 shrink-0 transition-transform duration-150 ease-out motion-reduce:transition-none',
        open && 'rotate-180',
      )}
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

/**
 * A figure with its unit, in the shape `/revenue-tracker` already uses.
 *
 * 🔴 **COMPACTED WITH K/M SINCE 2026-09-13 — owner's decision, every money figure
 * on the Management Dashboard.** `compactMoneyParts` is the ONE compact formatter
 * in this app; a local `Intl.NumberFormat` here would be a second one, and two
 * would drift on the first correction into one tab reading differently from the
 * next.
 *
 * ⛔ **THE EXACT DECIMAL SURVIVES ON THE `title`.** `1.20M` has discarded
 * everything below the hundred thousand, so two accounts £40,000 apart render
 * identically — and this table is RANKED, so it is read as a comparison. The
 * `title` is the only place the precise figure still exists, and the currency
 * code travels in it because a figure read out of context has to carry its own
 * unit.
 *
 * ⚠️ **NOTHING PARSES THIS BACK.** Every arithmetic reader on this tab — the
 * share column, the total, the ranking — takes the exact decimal string. Never
 * compare two compacted strings.
 *
 * ⚠️ **ONE COMPONENT FOR EVERY MONEY CELL** (the company row, its cycle detail
 * and its per-service breakdown), so a compacted figure and a long one cannot
 * end up beside each other, which is the inconsistency the compaction exists to
 * remove. Only the `K`/`M` is gold; `CompactFigure` owns that boundary and the
 * no-whitespace rule that keeps head, unit and tail one figure to a screen
 * reader and to a selection.
 */
function Money({
  amount,
  currency,
  strong,
}: {
  amount: string;
  currency: RevenueCurrency;
  strong?: boolean;
}): ReactElement {
  return (
    <span
      className="inline-flex items-baseline justify-end gap-1 whitespace-nowrap"
      title={`${formatDecimalString(amount)} ${currency}`}
    >
      {/*
       * ⛔ **NO CODE SPAN HERE — owner's instruction, 2026-09-13.** It rendered
       * `USD US$100.47K`, stating the unit twice in one cell, and the symbol
       * `compactMoneyParts` already emits is the clearer of the two.
       *
       * ⚠️ **THE UNIT IS NOT LOST, WHICH IS THE ONLY REASON THIS IS SAFE.** It
       * is stated on the panel header (`Paid, incl. tax · USD · …`), which is one
       * line above this column and applies to every row, and it still travels
       * on the `title` above — where it has to, because a figure read out of
       * context carries no header with it.
       *
       * ⛔ **DO NOT RESTORE IT IF A SECOND CURRENCY EVER APPEARS IN ONE TABLE.**
       * Every row here is in the same unit by construction — ⚠️ *SUPERSEDED
       * 2026-09-18, the MECHANISM and not the fact: "This whole tab FILTERS to a
       * single chosen currency."* It CONVERTS into one now, which reaches the
       * same guarantee from the other end and covers strictly more rows. The day
       * a table can hold two units the answer is a per-row unit again, not a
       * header. That is the condition to check before reading this as a style
       * preference.
       */}
      <span className={cx('text-fg tabular', strong ? 'font-semibold' : 'font-medium')}>
        <CompactFigure parts={compactMoneyParts(amount, currency)} />
      </span>
    </span>
  );
}

/**
 * The share cell: a bar and the percentage that is the real answer.
 *
 * ⚠️ The bar is `aria-hidden` and its width comes from a `Number()` — geometry,
 * never a figure. The percentage beside it is what a screen reader gets, and it
 * is an em dash rather than `0%` wherever no share could be computed.
 */
function ShareCell({ share }: { share: number | null }): ReactElement {
  const width = share === null || !Number.isFinite(share) ? 0 : Math.min(100, Math.max(0, share));

  return (
    <span className="flex items-center justify-end gap-2">
      <span
        aria-hidden="true"
        className="hidden h-1.5 w-16 overflow-hidden rounded-xs bg-surface-sunken sm:block"
      >
        <span className="block h-full rounded-xs bg-fg-secondary" style={{ width: `${width}%` }} />
      </span>
      <span className="w-12 text-xs text-fg-secondary tabular">{shareText(share)}</span>
    </span>
  );
}

/**
 * One company's cycle-by-cycle detail, opened in place.
 *
 * ⚠️ **IT REACHES THE READER WITHOUT LEAVING THE TAB, which is the point.** The
 * alternative — a link out to `/revenue-tracker` — loses the currency, the
 * ranking and the filters the reader arrived with, and lands them in a grid
 * they then have to find the company in.
 *
 * ⚠️ **A cycle with nothing in this currency is ABSENT rather than empty.** An
 * expanded company shows the years it traded; a run of em dashes for the years
 * it did not is noise sitting between the reader and the answer.
 */
function CompanyLines({
  entry,
  scopeCycles,
  currency,
  visibility,
}: {
  entry: ScopedRow;
  scopeCycles: readonly RevenueCycle[];
  currency: RevenueCurrency;
  visibility: MatrixVisibilityProps;
}): ReactElement {
  /*
   * ⚠️ **THE SAME HIDDEN YEARS AND PRODUCTS AS THE MATRIX** (owner, 2026-10-27):
   * a hidden year's section and a hidden product's lines are dropped, and a
   * year's figure is re-added over the lines that remain — `sumDecimalStrings`,
   * exact — so the heading never states money the list beneath it hides. A year
   * left with no visible line is dropped like a year with no revenue. A line's
   * product is named by the matrix's own grouping (`groupKeyOf`), so the two
   * views can never disagree about which row a line belongs to.
   */
  const addOnKeys = visibility.addOns?.keys ?? null;
  const allDetails = cycleDetailsOf(entry.row, scopeCycles, currency);
  const keyOfService = (service: (typeof allDetails)[number]['services'][number]): string => {
    const product = service.product ?? null;
    return groupKeyOf({
      cycleId: '',
      productId: product === null ? null : String(product.id),
      productName: product === null ? null : product.name,
      isUncatalogued: service.isUncatalogued ?? false,
      lineDescription: service.lineDescription ?? null,
      amount: '0',
      invoiceNumber: null,
      invoiceId: null,
    }).key;
  };
  const details: { cycle: RevenueCycle; amount: string | null; services: typeof allDetails[number]['services'] }[] =
    allDetails.flatMap((detail) => {
      if (!isKeyShown(cycleColumnKey(String(detail.cycle.id)), visibility.cycles.hidden)) return [];
      const services = detail.services.filter((service) =>
        isKeyShown(keyOfService(service), visibility.rows.hidden, addOnKeys),
      );
      if (services.length === detail.services.length) return [detail];
      if (services.length === 0) return [];
      const figures = services.map(paidFigureOf).filter((figure): figure is string => figure !== null);
      return [{ ...detail, services, amount: figures.length === 0 ? null : sumDecimalStrings(figures) }];
    });

  if (details.length === 0 && allDetails.length > 0) {
    // Show everything THIS company has — its years and its products — not the
    // reader's choices for companies that are not open.
    const showEverything = () => {
      visibility.cycles.setHidden(
        allDetails.map((detail) => cycleColumnKey(String(detail.cycle.id))),
        false,
      );
      visibility.rows.setHidden(
        [...allDetails.flatMap((detail) => detail.services.map(keyOfService)), HIDE_ADD_ONS_ID],
        false,
      );
    };
    return (
      <EmptyState
        title="Everything here is hidden"
        action={
          <Button size="sm" variant="secondary" onClick={showEverything}>
            Show all
          </Button>
        }
      />
    );
  }

  return (
        <div className="flex flex-col gap-3">
          {details.map((detail) => (
            <div key={detail.cycle.id} className="min-w-0">
              <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-border pb-1">
                <span className="text-xs font-semibold text-fg">{detail.cycle.name}</span>
                {detail.amount === null ? (
                  <span className="text-xs text-fg-muted">—</span>
                ) : (
                  <Money amount={detail.amount} currency={currency} strong />
                )}
              </div>

              {/*
               * ⛔ **LISTED, NEVER GROUPED AND NEVER SUMMED BY NAME.** Two
               * lines under one project read IDENTICALLY and are still two
               * sales with their own amounts and invoice numbers — see
               * `cycleDetailsOf`. The index is part of the key for exactly that
               * reason: the name is not unique and must never be a key.
               */}
              <ul className="m-0 flex list-none flex-col p-0">
                {detail.services.map((service, index) => (
                  <li
                    key={`${detail.cycle.id}:${service.invoiceId ?? 'x'}:${index}`}
                    className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5 py-1"
                  >
                    <span className="min-w-0 text-xs text-fg-secondary [overflow-wrap:anywhere]">
                      {service.serviceName}
                      {/*
                       * ⚠️ `invoiceNumber` and `invoiceId` are INDEPENDENTLY
                       * nullable and are read independently: an invoice raised
                       * inside Xero and linked here can carry a number with no
                       * local id, and a local draft an id with no number. So
                       * the number renders when there is one and the link is
                       * offered when there is an id, and neither implies the
                       * other.
                       *
                       * ⚠️ **THE ID-LESS BRANCH IS UNCHANGED AND MUST STAY
                       * THAT WAY.** `InvoiceNumberLink` is mounted only where
                       * BOTH halves exist — it needs the id for its `href` and
                       * for the read — so a number with no id is still plain
                       * text with no affordance, which is the honest render of
                       * a document this app cannot open.
                       */}
                      {service.invoiceNumber !== null && (
                        <span className="ml-2 text-2xs text-fg-subtle">
                          {service.invoiceId === null ? (
                            service.invoiceNumber
                          ) : (
                            /*
                             * ⚠️ **A LINK THAT OPENS THE EDIT FORM IN PLACE —
                             * owner's instruction.** The `href` is still
                             * `/invoices/{id}`, so a modified click still opens
                             * the detail page in a tab; only the plain click is
                             * intercepted. The form itself is mounted ONCE, by
                             * `InvoiceQuickEdit` in `RevenueByCompanyPanel` —
                             * one per surface, never one per row.
                             */
                            <InvoiceNumberLink
                              className={LINK_CLASS}
                              invoiceId={service.invoiceId}
                              invoiceNumber={service.invoiceNumber}
                            />
                          )}
                        </span>
                      )}
                    </span>
                    {/*
                     * The same per-line figure `revenueFor` summed into the
                     * cycle total above — paid, incl. tax. `null` is a line the
                     * server could not price with tax, left out of that total.
                     */}
                    {(() => {
                      const figure = paidFigureOf(service);
                      return figure === null ? (
                        <span className="text-xs text-fg-muted">—</span>
                      ) : (
                        <Money amount={figure} currency={currency} />
                      );
                    })()}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
  );
}

/** `true` when two decimal strings are the same amount, whatever their scale. */
function sameAmount(a: string | null, b: string | null): boolean {
  if (a === null || b === null) return a === b;
  const negated = b.trim().startsWith('-') ? b.trim().slice(1) : `-${b.trim()}`;
  const difference = sumDecimalStrings([a, negated]);
  return difference !== null && /^-?0(\.0+)?$/.test(difference);
}

const EXPAND_VIEW_OPTIONS = [
  { value: 'table', label: 'Table' },
  { value: 'lines', label: 'Lines' },
] as const;

/**
 * One company's products × cycles matrix.
 *
 * 🔴 **BUILT FROM EXACTLY THE LINES THE LINES VIEW LISTS** — `matrixLinesOf`
 * walks `cycleDetailsOf` — so its grand total is the company row's figure. The
 * dev-only check below says so loudly the day the two disagree.
 */
function CompanyMatrix({
  entry,
  scopeCycles,
  todaysCycleId,
  currency,
  visibility,
}: {
  entry: ScopedRow;
  scopeCycles: readonly RevenueCycle[];
  todaysCycleId: number | null;
  currency: RevenueCurrency;
  visibility: MatrixVisibilityProps;
}): ReactElement {
  const matrix = useMemo(
    () =>
      buildRevenueMatrix(
        matrixLinesOf(entry.row, scopeCycles, currency),
        matrixCyclesOf(scopeCycles),
        { currentCycleId: todaysCycleId === null ? null : String(todaysCycleId) },
      ),
    [entry.row, scopeCycles, currency, todaysCycleId],
  );

  useEffect(() => {
    if (!import.meta.env.DEV) return;
    if (!sameAmount(matrix.grandTotal, entry.amount)) {
      console.error('[RevenueCompanyTable] matrix grand total differs from the company row total', {
        company: entry.row.company.name,
        matrixGrandTotal: matrix.grandTotal,
        rowTotal: entry.amount,
      });
    }
  }, [matrix, entry.amount, entry.row.company.name]);

  return (
    <CompanyRevenueMatrix
      matrix={matrix}
      currencyCode={currency}
      companyName={entry.row.company.name}
      visibility={visibility}
    />
  );
}

/**
 * One company's detail, opened in place: the products × cycles matrix by
 * default, or the per-cycle list of lines.
 *
 * ⚠️ **THE MATRIX IS WITHHELD AGAINST A SERVER THAT DOES NOT REPORT PRODUCTS**
 * (`reportsProducts`) — its rows would all be the no-product bucket. The Lines
 * view is then shown with no toggle.
 *
 * ⚠️ **`w-0 min-w-full` ON THE WRAPPER IS WHAT KEEPS A WIDE MATRIX SCROLLING
 * INSIDE THE CELL** rather than widening the whole ranked table: a zero width
 * contributes nothing to the auto-layout table's column sizing, and `min-w-full`
 * then stretches it to the cell.
 */
function CompanyDetail({
  entry,
  scopeCycles,
  todaysCycleId,
  currency,
  columnCount,
  view,
  onViewChange,
  visibility,
}: {
  entry: ScopedRow;
  scopeCycles: readonly RevenueCycle[];
  todaysCycleId: number | null;
  currency: RevenueCurrency;
  columnCount: number;
  view: CompanyExpandView;
  onViewChange: (view: CompanyExpandView) => void;
  /** The table's hidden years and products — one instance for every expanded company. */
  visibility: MatrixVisibilityProps;
}): ReactElement {
  const matrixAvailable = reportsProducts(entry.row);
  const shown: CompanyExpandView = matrixAvailable ? view : 'lines';

  return (
    <TR className="bg-surface-sunken">
      <TD colSpan={columnCount} verticalAlign="top" className="px-4 py-3">
        <div className="flex w-0 min-w-full flex-col gap-3">
          {matrixAvailable && (
            <div className="flex justify-end">
              <ViewToggle
                label={`How to show what ${entry.row.company.name} bought`}
                value={view}
                options={EXPAND_VIEW_OPTIONS}
                onChange={onViewChange}
              />
            </div>
          )}

          {shown === 'table' ? (
            /*
             * ⚠️ `isolate`: the nested matrix's header cells inherit the outer
             * table's sticky `z-[15]`, and without their own stacking context
             * they would paint over the outer pinned header row as it scrolls.
             */
            <div className="isolate min-w-0 overflow-x-auto">
              <CompanyMatrix
                entry={entry}
                scopeCycles={scopeCycles}
                todaysCycleId={todaysCycleId}
                currency={currency}
                visibility={visibility}
              />
            </div>
          ) : (
            <CompanyLines
              entry={entry}
              scopeCycles={scopeCycles}
              currency={currency}
              visibility={visibility}
            />
          )}
        </div>
      </TD>
    </TR>
  );
}

export function RevenueCompanyTable({
  ordered,
  scopeCycles,
  todaysCycleId,
  currency,
  converted,
  total,
  scopeLabel,
  loading,
  narrowed,
  onClearFilters,
  search,
  onSearchChange,
}: {
  /**
   * The companies on screen, in the READER'S chosen order.
   *
   * ⚠️ **NOT NECESSARILY REVENUE ORDER**, which is why every row carries its
   * own `rank` — see {@link ScopedRow}. Nothing in this file may infer a
   * ranking from a row's position.
   */
  ordered: readonly ScopedRow[];
  /** The cycles the figures cover — every cycle, or the one that was chosen. */
  scopeCycles: readonly RevenueCycle[];
  /** The id of the cycle containing today, or `null` when today is in none. */
  todaysCycleId: number | null;
  /**
   * ⚠️ **THE UNIT THE FIGURES ARE IN — the server's echoed
   * `meta.measuredInCurrency` where it converted, the picker's value where it
   * did not.** Never the string that was requested: this code is rendered beside
   * every amount, and naming the wrong one is the failure the echo exists to
   * prevent.
   */
  currency: RevenueCurrency;
  /**
   * Whether the SERVER said it converted — i.e. whether `meta.measuredInCurrency`
   * came back.
   *
   * 🔴 **IT DECIDES EXACTLY ONE SENTENCE, AND THAT SENTENCE IS A CLAIM ABOUT THE
   * BUSINESS.** The empty state used to read *"No paid invoice in GBP falls in
   * this scope"*, which was true while the currency FILTERED and is false the
   * moment it converts: a dollar invoice converted into pounds IS a figure in
   * pounds, so the sentence denies the existence of revenue that would have been
   * counted had there been any. It also sends the reader to a control that cannot
   * help — the dead instruction this repo already records against
   * `excludedByCurrency`.
   *
   * ⛔ **Read it off the echo, never off whether a base was requested.** An older
   * server ignores the key and really does filter, and there the old sentence is
   * the correct one.
   */
  converted: boolean;
  /** The exact sum of the rows, which every share is taken against. */
  total: string | null;
  /** What the reader chose, for the heading — "All cycles" or a cycle's name. */
  scopeLabel: string;
  loading: boolean;
  /**
   * Whether anything — a filter OR the cycle — is narrowing the list, for the
   * empty state's wording. Wider than the bar's advanced count on purpose.
   */
  narrowed: boolean;
  onClearFilters: () => void;
  /** The search box beside Columns; the panel owns the value (URL key `rcQ`) and filters `ordered`. */
  search: string;
  onSearchChange: (value: string) => void;
}): ReactElement {
  const [expandedKeys, setExpandedKeys] = useRememberedState<readonly string[]>(
    'revenue-company-expanded',
    [],
    parseStringArray,
  );
  const expanded = useMemo<ReadonlySet<string>>(() => new Set(expandedKeys), [expandedKeys]);
  const [showAll, setShowAll] = useRememberedState('revenue-company-show-all', false);

  /*
   * How an expanded company is shown — one choice for the whole table,
   * remembered on the account (filter memory), seeded from this browser's older
   * local choice. Table (the matrix) unless the reader picked Lines.
   */
  const [expandView, setExpandView] = useRememberedState<CompanyExpandView>(
    'revenue-company-expand-view',
    readCompanyExpandView,
    (raw) => (raw === 'lines' || raw === 'table' ? raw : undefined),
  );
  const changeExpandView = (view: CompanyExpandView) => {
    setExpandView(view);
    writeCompanyExpandView(view);
  };

  /*
   * ⚠️ **KEYED ON `row.key`, NEVER ON THE COMPANY NAME.** `key` is the row's
   * own identity from the server; two companies can legitimately share a name
   * (this estate holds a pair of them), and keying on the name would open two
   * detail rows from one click.
   */
  const toggle = (key: string) => {
    setExpandedKeys((current) =>
      current.includes(key) ? current.filter((open) => open !== key) : [...current, key],
    );
  };

  /*
   * ⚠️ **A SINGLE CYCLE IN SCOPE MAKES THE COUNT A LINE OF ONES**, which says
   * nothing, so it is not rendered then. Derived from the scope rather than
   * from a second flag, so the note and the figures beside it cannot disagree
   * about what is on screen.
   */
  const showCycleCount = scopeCycles.length > 1;

  /*
   * The reader's own arrangement of this table — order, hidden set, frozen
   * edges — read from and written back to their account.
   */
  const layout = useColumnLayout(LAYOUT_SCOPE, COLUMN_KEYS, LOCKED_KEYS);

  /*
   * What the reader has hidden INSIDE an expanded company — years and products,
   * plus the "Hide add-ons" switch (owner, 2026-10-27). Mounted HERE, once, so
   * every expanded company reads the same choice; see `matrix-visibility.ts`.
   * ⚠️ This ranked table itself has no per-year columns — it shows one figure
   * per company for the panel's cycle filter, which stays the control for that.
   */
  const matrixCycles = useMatrixVisibility(MATRIX_VISIBILITY_SCOPES.companyMatrixCycles);
  const matrixProducts = useMatrixVisibility(MATRIX_VISIBILITY_SCOPES.companyMatrixProducts);
  const addOns = useAddOnProductRowKeys();
  const matrixVisibility = useMemo<MatrixVisibilityProps>(
    () => ({ cycles: matrixCycles, rows: matrixProducts, rowsTitle: 'Products', addOns }),
    [matrixCycles, matrixProducts, addOns],
  );

  /*
   * ⚠️ **THE OFFSETS ARE MEASURED FROM THE HEADER ROW, because there is no
   * `table-fixed` and no `<colgroup>` here** — a `w-*` on a `<th>` is a request
   * the content can outvote, so the only honest source for how far a frozen
   * column sits from its edge is the DOM.
   */
  const headRowRef = useRef<HTMLTableRowElement>(null);
  const frozenOffsets = useFrozenOffsets(headRowRef, layout.visibleKeys, layout.frozenSideOf);

  /*
   * 🔴 **THE DETAIL ROW'S `colSpan` IS THE LIVE VISIBLE COUNT, NEVER A LITERAL.**
   *
   * ⚠️ *SUPERSEDED: "**FIXED AT FIVE.** The table used to have two shapes — six
   * columns with the cycle count, five without — and a detail row that had to be
   * told which. One shape is one `colSpan`."* **The table has as many shapes as
   * there are subsets of its columns now**, so a literal is wrong the moment
   * anybody hides one — and it is wrong SILENTLY: a `colSpan` too large makes
   * the expanded row overhang the table, one too small draws a rule that stops
   * short. Nothing errors, `tsc` is green, and the only symptom is a line in the
   * wrong place. The count is derived from the very list the header and the body
   * are built from, so the three cannot disagree.
   */
  const columnCount = layout.visibleKeys.length;

  const visible = showAll ? ordered : ordered.slice(0, HEAD_ROWS);
  const hidden = ordered.length - visible.length;

  /*
   * ⚠️ **THE CELL FOR EACH KEY IS A SWITCH, NOT A LOOKUP OBJECT.** Every arm
   * below keeps the comment that was attached to the header or cell it
   * replaces — those paragraphs are the record of why each width, each control
   * and each mark is what it is, and a table of JSX would have stranded them
   * from the markup they describe.
   *
   * ⚠️ **THE HEADER AND THE BODY WALK THE SAME LIST.** Two lists is how a table
   * ends up with a heading over the wrong column, which is a failure no type
   * and no build can see.
   */
  const headCell = (key: string): ReactElement | null => {
    const frozen = frozenPropsFor(key, layout.frozenSideOf, frozenOffsets);

    switch (key) {
      case 'rank':
        return (
          <TH key={key} numeric className="w-12" {...frozen}>
            {/*
             * ⚠️ A screen reader announces "#" as "number sign", which
             * says nothing about the column. The word is given to
             * assistive tech and the symbol to the eye — the same fact,
             * twice, rather than a heading that is legible to only one of
             * them.
             *
             * ⚠️ **"Revenue rank", because that is what it stays under
             * every ordering.** Sorted A to Z the numbers are scattered,
             * which is correct and is the only thing telling a sighted
             * reader the column is not a row number.
             */}
            <span className="sr-only">Revenue rank</span>
            <span aria-hidden="true">#</span>
          </TH>
        );
      case 'company':
        return (
          <TH key={key} {...frozen}>
            Company
          </TH>
        );
      case 'region':
        return (
          <TH key={key} {...frozen}>
            Billing region
          </TH>
        );
      case 'revenue':
        return (
          <TH key={key} numeric className="w-40" {...frozen}>
            Paid revenue
          </TH>
        );
      case 'share':
        return (
          <TH key={key} numeric className="w-32" {...frozen}>
            Share
          </TH>
        );
      default:
        return null;
    }
  };

  const bodyCell = (key: string, entry: ScopedRow, isOpen: boolean): ReactElement | null => {
    const frozen = frozenPropsFor(key, layout.frozenSideOf, frozenOffsets);

    switch (key) {
      case 'rank':
        return (
          /*
           * ⚠️ **`entry.rank`, NEVER the map index.** The rows can
           * be ordered by name or smallest-first, and a positional
           * index would then print a confident number that ranks
           * nothing. See `ScopedRow.rank`.
           */
          <TD key={key} numeric className="text-xs text-fg-subtle tabular" {...frozen}>
            {entry.rank}
          </TD>
        );

      case 'company':
        return (
          <TD key={key} {...frozen}>
            <div className="flex min-w-0 flex-col gap-1">
              <div className="flex min-w-0 items-start gap-2">
                {/*
                 * ⚠️ **The disclosure is a BUTTON and the company is
                 * a LINK, and they are separate controls.** Making
                 * the whole row a button would swallow the link;
                 * making the whole row a link would put the detail
                 * behind a navigation. Each control does one thing
                 * and says which.
                 *
                 * 🔴 **AND IT IS WHY THIS COLUMN IS LOCKED.** It is the
                 * only route into the detail row, so a reader who could
                 * hide this column could hide the capability. See
                 * `LOCKED_KEYS`.
                 */}
                <Tooltip label={`${isOpen ? 'Hide' : 'Show'} what ${entry.row.company.name} bought`}>
                  <button
                    type="button"
                    aria-expanded={isOpen}
                    aria-label={`${isOpen ? 'Hide' : 'Show'} what ${entry.row.company.name} bought`}
                    onClick={() => toggle(entry.row.key)}
                    className="mt-0.5 shrink-0 rounded-sm p-0.5 text-fg-subtle transition-colors duration-150 ease-out hover:text-fg motion-reduce:transition-none"
                  >
                    <ChevronIcon open={isOpen} />
                  </button>
                </Tooltip>

                <TextLink
                  to={`/companies/${entry.row.company.id}`}
                  className="min-w-0 text-sm font-medium [overflow-wrap:anywhere]"
                >
                  {entry.row.company.name}
                </TextLink>
              </div>

              {/*
               * 🔴 **THE ONLY MARK SAYING THE FIGURE BESIDE IT IS
               * PARTIAL — ON AN UNCONVERTED RESPONSE.** This company
               * was paid in another currency too, and appears on
               * that currency's list with a different number.
               * Neither is "what they pay us", and nothing converts
               * between them.
               *
               * 🔴 **EMPTY ON EVERY CONVERTED RESPONSE, WHICH IS
               * EVERY ORDINARY ONE SINCE 2026-09-18** — the sibling
               * row it pointed at has been summed into this one, so
               * the figure is no longer partial and a badge calling
               * it partial would send a reader looking for a row
               * that does not exist.
               *
               * ⚠️ **IT SUPPRESSES ITSELF THROUGH THE DATA RATHER
               * THAN A FLAG, and that is what makes it honest.**
               * `currenciesUsedBy` answers `[base]` when every line
               * really is in the base, so the mark returns of its
               * own accord against a server predating
               * `baseCurrency` — which is exactly the response that
               * still needs it. **Do not gate this on `converted`**:
               * that would take it away on a response where two
               * currencies are still never added.
               */}
              {entry.alsoBilledIn.length > 0 && (
                <span className="flex flex-wrap gap-1 pl-6">
                  {entry.alsoBilledIn.map((code) => (
                    /*
                     * ⚠️ **THE LABEL CARRIES THE WHOLE FACT AND
                     * THERE IS NO `title`.** `Badge`'s own rule is
                     * that a `title` holds a VALUE a short label
                     * hides, never advice — and a tooltip is
                     * invisible on touch, so nothing a reader needs
                     * may live there alone. This reader needs it.
                     */
                    <Badge key={code} tone="neutral" variant="outline">
                      Also paid in {code}
                    </Badge>
                  ))}
                </span>
              )}

              {/*
               * ⚠️ **WHAT THE "Cycles" COLUMN USED TO SAY, where it
               * belongs.** It answers whether a big figure is a
               * durable account or one good year, which is a fact
               * about the COMPANY rather than a figure to be read
               * alongside the money — so it sits under the name and
               * in muted text, not in a numeric column competing
               * with two that carry amounts. See this file's
               * header.
               *
               * ⚠️ **`1 cycle` IS RENDERED, NOT SUPPRESSED.** A
               * one-cycle account is the most interesting value
               * this line has: it is exactly the one-off the
               * minimum-revenue floor exists to hide.
               *
               * ⛔ **AND IT IS NOT A COLUMN NOW EITHER.** The column
               * manager below offers no key for it, deliberately —
               * see this file's header for why it was removed.
               */}
              {showCycleCount && (
                <span className="pl-6 text-2xs text-fg-subtle tabular">
                  {entry.activeCycles.toLocaleString()}{' '}
                  {entry.activeCycles === 1 ? 'cycle' : 'cycles'}
                </span>
              )}
            </div>
          </TD>
        );

      case 'region':
        return (
          <TD key={key} className="text-xs text-fg-muted [overflow-wrap:anywhere]" {...frozen}>
            {regionLabelOf(entry.row)}
          </TD>
        );

      case 'revenue':
        return (
          <TD key={key} numeric {...frozen}>
            <Money amount={entry.amount} currency={currency} strong />
          </TD>
        );

      case 'share':
        return (
          <TD key={key} numeric {...frozen}>
            <ShareCell share={shareOf(entry.amount, total)} />
          </TD>
        );

      default:
        return null;
    }
  };

  return (
    <Panel className="min-w-0">
      <PanelHeader
        title="Revenue by company"
        /*
         * The heading carries the three qualifiers every figure below depends
         * on. A table headed "Revenue" is a table somebody quotes as revenue.
         */
        description={`Paid, incl. tax · ${currency} · ${scopeLabel} · ${ordered.length.toLocaleString()} ${
          ordered.length === 1 ? 'company' : 'companies'
        }`}
        /*
         * ⚠️ **THE TRIGGER LIVES IN THIS PANEL'S OWN HEADER, which is rendered
         * here rather than by the tab above** — so the control reaches the
         * table it configures without a parent having to pass anything down.
         *
         * ⚠️ **WITHHELD UNTIL THE STORED LAYOUT HAS ARRIVED, and that is not
         * cosmetic.** Until the read resolves the controller holds the DEFAULT
         * layout, so a toggle pressed in that window would persist a hidden set
         * derived from nothing and overwrite the arrangement still in flight —
         * a `200`, no error, and a column the reader had hidden quietly back.
         *
         * ⚠️ **IT IS NOT GATED ON THE ROWS.** It renders over the empty state
         * too, because unhiding a column is the only way back for somebody who
         * hid one and then filtered the list to nothing.
         */
        actions={
          <div className="flex items-center gap-2 max-sm:w-full">
            <Input
              type="search"
              aria-label="Search"
              placeholder="Search…"
              className="w-56 max-sm:w-full"
              value={search}
              onChange={(event) => onSearchChange(event.target.value)}
            />
            {!layout.loading && (
              <ColumnManager layout={layout} labelOf={labelOf} lockedKeys={LOCKED_KEYS} />
            )}
          </div>
        }
      />

      {loading ? (
        <div className="flex flex-col gap-2 px-4 py-4">
          {[0, 1, 2, 3, 4, 5].map((n) => (
            <Skeleton key={n} className="h-6 w-full" />
          ))}
        </div>
      ) : ordered.length === 0 ? (
        <EmptyState
          title={
            narrowed
              ? 'No companies match these filters'
              : /*
                 * 🔴 **THE CURRENCY LEAVES THE TITLE UNDER CONVERSION, because
                 * naming it would blame a control that narrowed nothing.** With
                 * every currency converted into the base, an empty table means
                 * there is no paid revenue in this scope at all — a fact about
                 * the business — where under a FILTER it meant this currency had
                 * none, a fact about the control.
                 */
                converted
                ? 'No revenue to report in this scope'
                : `No ${currency} revenue to report`
          }
          description={
            narrowed
              ? 'Clear the filters to see every company again.'
              : converted
                ? /*
                   * ⚠️ **NAMES THE CYCLE SCOPE RATHER THAN THE CURRENCY**, which
                   * is the only control left that could have emptied this. It is
                   * a consequence and not a next step, exactly as the sentence
                   * below it is.
                   */
                  'No paid invoice falls in this scope, in any currency.'
                : /*
                   * ⚠️ **NAMES THE CURRENCY, because on an UNCONVERTED response
                   * it is the one control whose effect a reader cannot see**: an
                   * empty page under GBP looks exactly like a business that
                   * traded nothing.
                   *
                   * ⚠️ A CONSEQUENCE AND NOT A NEXT STEP — "the other currencies
                   * are on the control above" was written here and removed. The
                   * control is on screen; telling somebody to use it is the
                   * helper text this app's copy rules refuse.
                   */
                  `No paid invoice in ${currency} falls in this scope.`
          }
          {...(narrowed
            ? {
                action: (
                  <Button variant="secondary" onClick={onClearFilters}>
                    Clear filters
                  </Button>
                ),
              }
            : {})}
        />
      ) : (
        /*
         * The tab's main table: the header row stays pinned while it scrolls
         * in its own viewport-tall area (owner 2026-10-18).
         */
        <TableWrap frozenHeader>
          <Table>
            <THead>
              {/*
               * The row `useFrozenOffsets` measures. See the note at the hook
               * call — the widths are only knowable from the DOM.
               */}
              <TR ref={headRowRef}>{layout.visibleKeys.map((key) => headCell(key))}</TR>
            </THead>

            <TBody>
              {visible.map((entry) => {
                const isOpen = expanded.has(entry.row.key);

                return (
                  <Fragment key={entry.row.key}>
                    <TR>{layout.visibleKeys.map((key) => bodyCell(key, entry, isOpen))}</TR>

                    {isOpen && (
                      <CompanyDetail
                        entry={entry}
                        scopeCycles={scopeCycles}
                        todaysCycleId={todaysCycleId}
                        currency={currency}
                        columnCount={columnCount}
                        view={expandView}
                        onViewChange={changeExpandView}
                        visibility={matrixVisibility}
                      />
                    )}
                  </Fragment>
                );
              })}
            </TBody>
          </Table>
        </TableWrap>
      )}

      {/*
       * ⚠️ **THE HIDDEN COUNT IS STATED, NOT IMPLIED.** A truncated ranked list
       * is an ordinary analytical affordance; a truncated one that does not say
       * how much it is holding back is indistinguishable from a filter that
       * matched little — the failure this repo's own export rules refuse. The
       * control names the number it will reveal.
       *
       * ⚠️ **IT TOGGLES BOTH WAYS.** A one-way "Show all" leaves a reader who
       * pressed it stuck scrolling 122 rows for the rest of the session, with
       * no control on screen to undo what they just did.
       */}
      {!loading && ordered.length > HEAD_ROWS && (
        <PanelFooter>
          <span className="text-xs text-fg-muted">
            {hidden > 0
              ? /*
                 * ⚠️ **"the first", NOT "the top".** Under *smallest first* or
                 * *A to Z* the rows on screen are not the top of anything, and
                 * a footer claiming they are would describe a ranking the
                 * reader has just turned off.
                 */
                `Showing the first ${visible.length.toLocaleString()} of ${ordered.length.toLocaleString()} companies.`
              : `Showing all ${ordered.length.toLocaleString()} companies.`}
          </span>
          <Button variant="secondary" onClick={() => setShowAll((open) => !open)}>
            {hidden > 0
              ? `Show all ${ordered.length.toLocaleString()}`
              : `Show first ${HEAD_ROWS}`}
          </Button>
        </PanelFooter>
      )}
    </Panel>
  );
}
